import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

import 'swiper/css';
import { SHOPIFY_CONFIG } from '@/partials/el.Config';

class SocialSection {
  constructor(section) {
    this.section = section;
    this.currentProductId = this.normaliseProductId(section.dataset.currentProductId);
    this.colourGroupIds = (section.dataset.colourGroupIds || '')
      .split(',')
      .map((id) => this.normaliseProductId(id))
      .filter(Boolean);
    this.targetProductIds = new Set([this.currentProductId, ...this.colourGroupIds].filter(Boolean));

    this.swiperContainer = section.querySelector('.el-social-section__swiper');
    this.slider = section.querySelector('.el-social-section__slides');
    this.prevButton = section.querySelector('.el-social-section__nav-button--prev');
    this.nextButton = section.querySelector('.el-social-section__nav-button--next');

    this.storefrontToken = SHOPIFY_CONFIG.STOREFRONT_TOKEN;
    this.storeUrl = SHOPIFY_CONFIG.SHOPIFY_STORE_URL;

    this.allItemIds = [];
    this.matchedItems = [];
    this.visibleItems = [];
    this.totalSlides = 0;
    this.isFullyLoaded = false;

    if (!this.slider || this.targetProductIds.size === 0) {
      return;
    }

    this.fetchData();
  }

  normaliseProductId(value) {
    if (!value) {
      return '';
    }
    const stringValue = String(value).trim();
    if (!stringValue) {
      return '';
    }
    if (stringValue.startsWith('gid://')) {
      return stringValue;
    }
    return `gid://shopify/Product/${stringValue}`;
  }

  async fetchData() {
    await this.fetchAndFilterItems();
    this.sortMatchedItems();

    this.visibleItems = this.matchedItems.filter((item) => this.getImageFromItem(item));
    this.totalSlides = this.visibleItems.length;

    if (this.totalSlides === 0) {
      return;
    }

    this.section.style.display = 'block';
    this.updateNavVisibility();
    this.waitForScroll();
  }

  waitForScroll() {
    if (window.Shopify?.designMode) {
      this.renderAndInit();
      return;
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            obs.disconnect();
            this.renderAndInit();
          }
        },
        { rootMargin: '400px 0px' },
      );
      observer.observe(this.section);
    } else {
      this.renderAndInit();
    }
  }

  renderAndInit() {
    if (this.isFullyLoaded) {
      return;
    }
    this.isFullyLoaded = true;

    this.renderSlides();
    this.initSlider();
  }

  initSlider() {
    if (!this.swiperContainer) {
      return;
    }

    this.swiperInstance = new Swiper(this.swiperContainer, {
      modules: [Navigation],
      slidesPerView: 1,
      loop: this.totalSlides > 1,
      speed: 600,
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
    });
  }

  updateNavVisibility() {
    const hasMultiple = this.totalSlides > 1;
    this.prevButton?.classList.toggle('is-hidden', !hasMultiple);
    this.nextButton?.classList.toggle('is-hidden', !hasMultiple);
  }

  getResizedUrl(url, width) {
    try {
      const newUrl = new URL(url);
      newUrl.searchParams.set('width', width);
      return newUrl.toString();
    } catch (e) {
      return url;
    }
  }

  generateSrcset(url) {
    const widths = [375, 550, 750, 900, 1100];
    return widths.map((w) => `${this.getResizedUrl(url, w)} ${w}w`).join(', ');
  }

  renderSlides() {
    const fragment = document.createDocumentFragment();

    this.visibleItems.forEach((item, index) => {
      const image = this.getImageFromItem(item);
      if (!image) {
        return;
      }

      const handle = item.handle?.value?.trim() || '';
      const profileUrl = item.profileUrl?.value || '';
      const originalUrl = image.url;

      const slide = document.createElement('div');
      slide.className = 'swiper-slide el-social-section__slide';
      slide.setAttribute('aria-hidden', String(index !== 0));

      const card = document.createElement('a');
      card.className = 'el-social-section__card';
      if (profileUrl) {
        card.href = profileUrl;
        card.target = '_blank';
        card.rel = 'noopener';
      } else {
        card.removeAttribute('href');
      }

      const imageElement = document.createElement('img');
      imageElement.className = 'el-social-section__image';
      imageElement.alt = image.altText || handle || 'Instagram image';
      imageElement.loading = 'lazy';
      imageElement.decoding = 'async';

      imageElement.srcset = this.generateSrcset(originalUrl);
      imageElement.src = this.getResizedUrl(originalUrl, 550);
      imageElement.sizes = '(min-width: 1200px) 540px, (min-width: 768px) 50vw, 100vw';

      imageElement.style.opacity = '0';
      imageElement.onload = () => {
        imageElement.style.transition = 'opacity 0.5s';
        imageElement.style.opacity = '1';
      };

      card.appendChild(imageElement);

      const overlay = document.createElement('div');
      overlay.className = 'el-social-section__overlay';

      const shadow = document.createElement('span');
      shadow.className = 'el-social-section__shadow';
      overlay.appendChild(shadow);

      if (handle) {
        const handleEl = document.createElement('span');
        handleEl.className = 'el-social-section__handle';
        handleEl.textContent = handle;
        overlay.appendChild(handleEl);
      }

      card.appendChild(overlay);
      slide.appendChild(card);
      fragment.appendChild(slide);
    });

    this.slider.innerHTML = '';
    this.slider.appendChild(fragment);
  }

  getImageFromItem(item) {
    return item.image?.reference?.image || null;
  }

  async fetchAndFilterItems() {
    const query = `
      query getInstagramItemsByType($type: String!) {
        metaobjects(type: $type, first: 250) {
          nodes {
            id
            handle: field(key: "instagram_handle") { value }
            profileUrl: field(key: "instagram_profile_url") { value }
            shouldShowOnProductDetailPage: field(key: "should_show_on_product_detail_page") { value }
            image: field(key: "image") {
              reference {
                ... on MediaImage {
                  image {
                    url
                    altText
                  }
                }
              }
            }
            productList: field(key: "product_list") {
              references(first: 20) {
                nodes { ... on Product { id } }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(`${this.storeUrl}/api/2024-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({
          query,
          variables: {
            type: 'sw_instagram_item',
          },
        }),
      });

      const json = await response.json();
      if (json.errors) {
        console.error('GraphQL Errors:', json.errors);
        return;
      }
      const items = json.data?.metaobjects?.nodes || [];
      const relevantItems = items.filter((item) => {
        const shouldShow = item.shouldShowOnProductDetailPage?.value;
        if (shouldShow === 'false') {
          return false;
        }

        const linkedProducts = item.productList?.references?.nodes || [];
        const hasMatchingProduct = linkedProducts.some((product) => this.targetProductIds.has(product.id));
        return hasMatchingProduct;
      });

      this.matchedItems = relevantItems;
    } catch (error) {
      console.error('Fetch error:', error);
    }
  }

  sortMatchedItems() {
    this.matchedItems.sort((itemA, itemB) => {
      const aHasCurrent = itemA.productList?.references?.nodes?.some((product) => product.id === this.currentProductId);
      const bHasCurrent = itemB.productList?.references?.nodes?.some((product) => product.id === this.currentProductId);

      if (aHasCurrent === bHasCurrent) {
        return 0;
      }
      return aHasCurrent ? -1 : 1;
    });
  }
}

const initSocialSections = () => {
  document.querySelectorAll('.el-social-section').forEach((section) => {
    if (!section.__socialSectionInstance) {
      section.__socialSectionInstance = new SocialSection(section);
    }
  });
};

elabs.onLoadSections(() => {
  initSocialSections();
});
